import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Square, Box, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PortfolioProject } from '@/lib/portfolio-data';
import { useLanguage } from '@/hooks/use-language';

interface FeaturedProjectsProps {
  projectsTitle: string;
  projectsSubtitle: string;
  projectsBtnUrl: string;
  projectsBtnText: string;
  featuredProjects: PortfolioProject[];
  activeProjectId: string | null;
  setActiveProjectId: React.Dispatch<React.SetStateAction<string | null>>;
}

export const FeaturedProjects: React.FC<FeaturedProjectsProps> = ({
  projectsTitle,
  projectsSubtitle,
  projectsBtnUrl,
  projectsBtnText,
  featuredProjects,
  activeProjectId,
  setActiveProjectId,
}) => {
  const { t, language } = useLanguage();

  return (
    <section className="py-16 lg:py-24 border-t border-white/5 relative">
      <div className="grid-cells-accent opacity-40" />
      <div className="container-custom relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-center lg:items-end gap-6 mb-12 lg:mb-16">
          <div className="space-y-4 text-center md:text-left">
            <h2
              className="font-extrabold uppercase tracking-tighter"
              dangerouslySetInnerHTML={{ __html: projectsTitle }}
            />
            <p className="text-white/40 uppercase tracking-[0.2em] text-[10px] lg:text-xs">{projectsSubtitle}</p>
          </div>
          <Link to={projectsBtnUrl} className="text-primary uppercase tracking-widest text-[10px] lg:text-xs font-bold hover:underline">
            {projectsBtnText} <Plus size={14} className="inline-block ml-1" />
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12">
          {featuredProjects.map((project, idx) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              onClick={() => {
                if (window.innerWidth <= 1024) {
                  setActiveProjectId(prev => prev === project.id ? null : project.id);
                }
              }}
              className={cn(
                "mirror-card group flex flex-row md:flex-col xl:flex-row h-full min-h-[160px] max-h-[200px] md:max-h-[520px] md:min-h-[520px] xl:max-h-[360px] xl:min-h-[360px] overflow-hidden cursor-pointer",
                activeProjectId === project.id && "is-active"
              )}
            >
              <div className="w-[42%] md:w-full xl:w-1/2 portfolio-image-wrapper md:h-[280px] xl:h-auto min-h-0 overflow-hidden flex-shrink-0 relative">
                <img
                  src={project.image}
                  alt={project.title[language as keyof typeof project.title]}
                  loading="lazy"
                  className="w-full h-full object-cover md:grayscale md:group-hover:grayscale-0 md:group-hover:scale-110 transition-all duration-700"
                />
                <div className="portfolio-grayscale-overlay" />
                <div className="absolute inset-0 bg-primary/10 opacity-0 lg:group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="mirror-reflection opacity-20 lg:group-hover:opacity-40 transition-opacity duration-500" />
                <div className="shine-overlay lg:group-hover:opacity-100 transition-opacity duration-700" />
              </div>
              
              <div className="w-[58%] md:w-full xl:w-1/2 p-4 md:p-6 lg:p-8 flex flex-col justify-between relative z-20 bg-[#13100B] bg-none opacity-100 backdrop-blur-none">
                <div className="space-y-3 md:space-y-6">
                  <h3 className="text-xs sm:text-sm md:text-xl lg:text-2xl font-bold uppercase tracking-tight group-hover:text-primary transition-colors leading-tight line-clamp-2 md:line-clamp-none">
                    {project.title[language as keyof typeof project.title]}
                  </h3>

                  <div className="space-y-2 md:space-y-4">
                    <div className="flex justify-between items-center text-[7px] md:text-[9px] lg:text-[10px] uppercase tracking-widest font-bold">
                      <span className="text-white/30 flex items-center gap-1.5 md:gap-2"><MapPin size={10} className="md:w-3 md:h-3" /> {t('portfolio.city_label')}</span>
                      <span className="text-white/80">{project.city[language as keyof typeof project.city]}</span>
                    </div>
                    <div className="flex justify-between items-center text-[7px] md:text-[9px] lg:text-[10px] uppercase tracking-widest font-bold">
                      <span className="text-white/30 flex items-center gap-1.5 md:gap-2"><Square size={10} className="md:w-3 md:h-3" /> {t('portfolio.area_label')}</span>
                      <span className="text-white/80">{project.area}</span>
                    </div>
                    <div className="flex justify-between items-center text-[7px] md:text-[9px] lg:text-[10px] uppercase tracking-widest font-bold">
                      <span className="text-white/30 flex items-center gap-1.5 md:gap-2"><Box size={10} className="md:w-3 md:h-3" /> {t('portfolio.product_label')}</span>
                      <span className="text-white/80 line-clamp-1">{project.product[language as keyof typeof project.product]}</span>
                    </div>
                  </div>
                </div>

                <Link
                  to={`/${language}/portfolio`}
                  onClick={(e) => e.stopPropagation()}
                  className="details-button mt-4 md:mt-8 flex items-center gap-2 md:gap-3 text-[8px] md:text-[10px] lg:text-xs font-bold uppercase tracking-widest text-primary hover:gap-5 transition-all duration-300 min-h-0 md:min-h-[44px]"
                >
                  {language === 'uz' ? 'Portfolioga' : language === 'en' ? 'To Portfolio' : 'В портфолио'} <Plus size={14} className="md:w-4 md:h-4" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
